/**
 * TriageTiles.jsx — the triage DECISION control (Matt, 2026-10-16: "it should feel meaningful to do
 * these triage actions rather than a weak little button press and lack of clarity with what it means").
 *
 * Three steps, one decision at a time:
 *   1. CHOOSE  — big square tiles, an icon and a plain label each, with one line saying what it does.
 *   2. CONFIRM — the chosen tile opens a panel that says exactly what will happen and to whom, asks for
 *                the one thing it needs (a person, a date, a sentence), and commits on a large button
 *                that names the act ("Take it", "Call it to Brandon", "Send the answer").
 *   3. DONE    — the row answers back in words ("Yours. First in your queue.") and then folds away.
 *
 * Pure UI: the caller supplies the options and does the work in onConfirm(key, value), which returns
 * a promise resolving to the done message. Used by the review-and-direction asks card and the
 * coordinators' requests card (CaptainReportGate.jsx).
 *
 * Option: { key, icon, label, sub, tone?: 'primary' | 'caution',
 *           step: { title, body, kind?: 'confirm' | 'text' | 'person' | 'date',
 *                   placeholder?, people?: [{id,name}], defaultValue?, min?,
 *                   confirm: (value) => string, confirmIcon?,
 *                   render?: (value, setValue) => node, ready?: (value) => bool  (kind 'custom') },
 *           disabled?, title?, open?: () => void  (opens a tool instead of a step) }
 *  A 'text' step with optional: true may be confirmed empty.
 *
 * question (2026-10-29, Matt: "something ambiguous about what I am being asked to do"): the one
 * question the tiles answer, shown above them. more: the rarer ways out (hand off, send back...),
 * same option shape, drawn as a quiet row under the tiles so they don't compete with the answer.
 */
import { useState } from 'react'

const ICONS = {
  mine: <><path d="M12 3v11" /><path d="M7.5 9.5 12 14l4.5-4.5" /><path d="M4 14v4.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V14" /></>,
  handoff: <><circle cx="8" cy="8" r="3" /><path d="M3 19c0-3 2.2-5 5-5s5 2 5 5" /><path d="M14 12h7" /><path d="m18 9 3 3-3 3" /></>,
  answer: <><path d="M4 5h16v11H9l-5 4z" /><path d="m8.5 10.5 2.5 2.5 4.5-4.5" /></>,
  sendback: <><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></>,
  chased: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /><path d="m9 15 2 2 4-4" /></>,
  cameback: <><path d="M4 8 12 4l8 4v8l-8 4-8-4z" /><path d="m4 8 8 4 8-4M12 12v8" /></>,
  escalate: <><path d="M12 20V5" /><path d="m6 11 6-6 6 6" /><path d="M5 20h14" /></>,
  open: <><path d="M14 4h6v6" /><path d="M20 4 11 13" /><path d="M18 14v4.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" /></>,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  info: <><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5.5" /><path d="M12 7.6v.2" /></>,
  did: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m8 12.3 2.8 2.8L16.5 9" /></>,
  ride: <><path d="M3 13.5 21 6l-4.5 13-4.5-5z" /><path d="m12 14 4.5-8" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  timecard: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 9h8M8 13h8M8 17h5" /></>,
  dayoff: <><circle cx="12" cy="12" r="8.5" /><path d="m8.5 8.5 7 7" /></>,
  lock: <><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" /></>,
  plus: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M12 8.5v7M8.5 12h7" /></>,
  snooze: <><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" /></>,
  back: <path d="M15 5 8 12l7 7" />,
  hold: <><circle cx="12" cy="12" r="8.5" /><path d="M10 9v6M14 9v6" /></>,
  stuck: <><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12h7" /></>,
  notreq: <><path d="M6 4h9l3 3v13H6z" /><path d="m9.5 11.5 5 5M14.5 11.5l-5 5" /></>,
  chat: <><path d="M4 5h16v11H9l-5 4z" /><path d="M8 9.5h8M8 12.5h5" /></>,
}
export function TileIcon({ name, size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name] || null}
    </svg>
  )
}

const CSS = `
.aj-tt-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(112px,1fr));gap:10px;margin-top:10px}
.aj-tt-tile{font:inherit;display:flex;flex-direction:column;align-items:flex-start;justify-content:space-between;gap:10px;min-height:112px;padding:14px 13px 12px;border-radius:10px;border:1px solid rgba(7,39,45,0.16);background:#fff;color:#07272D;cursor:pointer;text-align:left;transition:transform .16s ease,box-shadow .16s ease,border-color .16s ease,background .16s ease,color .16s ease}
.aj-tt-tile .aj-tt-ic{color:#5b8494;transition:color .16s ease,transform .2s ease}
.aj-tt-tile:hover:not(:disabled){transform:translateY(-3px);box-shadow:0 10px 22px rgba(7,39,45,0.14);border-color:#07272D}
.aj-tt-tile:hover:not(:disabled) .aj-tt-ic{color:#07272D;transform:scale(1.08)}
.aj-tt-tile:active:not(:disabled){transform:translateY(-1px) scale(.985);box-shadow:0 4px 10px rgba(7,39,45,0.12)}
.aj-tt-tile:focus-visible{outline:2px solid #b8540a;outline-offset:2px}
.aj-tt-tile:disabled{opacity:.45;cursor:default}
.aj-tt-tile.aj-tt-primary{background:#07272D;border-color:#07272D;color:#fff}
.aj-tt-tile.aj-tt-primary .aj-tt-ic{color:#f0b27a}
.aj-tt-tile.aj-tt-primary:hover:not(:disabled) .aj-tt-ic{color:#fff}
.aj-tt-tile.aj-tt-caution:hover:not(:disabled){border-color:#b8540a}
.aj-tt-tile.aj-tt-caution:hover:not(:disabled) .aj-tt-ic{color:#b8540a}
.aj-tt-lab{font-family:"DM Sans",sans-serif;font-size:14px;font-weight:600;line-height:1.2}
.aj-tt-sub{font-family:"DM Sans",sans-serif;font-size:11.5px;line-height:1.35;opacity:.72;margin-top:3px}
.aj-tt-panel{margin-top:10px;border-radius:10px;border:1px solid #07272D;background:#fff;padding:14px 16px;animation:aj-tt-in .22s ease both}
@keyframes aj-tt-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.aj-tt-ptitle{display:flex;align-items:center;gap:10px;font-family:"DM Sans",sans-serif;font-size:15px;font-weight:600;color:#07272D}
.aj-tt-pbody{font-family:"DM Sans",sans-serif;font-size:12.5px;line-height:1.5;color:#3a5260;margin:6px 0 12px}
.aj-tt-go{font:inherit;display:inline-flex;align-items:center;gap:9px;font-family:"DM Sans",sans-serif;font-size:14px;font-weight:600;padding:11px 18px;border-radius:8px;border:none;background:#07272D;color:#fff;cursor:pointer;transition:transform .14s ease,box-shadow .14s ease,opacity .14s ease}
.aj-tt-go:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 18px rgba(7,39,45,0.25)}
.aj-tt-go:disabled{opacity:.4;cursor:default}
.aj-tt-backbtn{font:inherit;display:inline-flex;align-items:center;gap:4px;font-family:"DM Sans",sans-serif;font-size:12.5px;color:#5b8494;background:none;border:none;cursor:pointer;padding:6px 4px}
.aj-tt-backbtn:hover{color:#07272D}
.aj-tt-people{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px}
.aj-tt-person{font:inherit;display:inline-flex;align-items:center;gap:8px;padding:6px 12px 6px 6px;border-radius:24px;border:1px solid rgba(7,39,45,0.18);background:#fff;color:#07272D;cursor:pointer;font-family:"DM Sans",sans-serif;font-size:13px;transition:border-color .14s ease,background .14s ease,transform .14s ease}
.aj-tt-person:hover{border-color:#07272D;transform:translateY(-1px)}
.aj-tt-person.on{background:#07272D;border-color:#07272D;color:#fff}
.aj-tt-av{width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;background:rgba(7,39,45,0.08);font-family:"DM Mono",monospace;font-size:10.5px}
.aj-tt-person.on .aj-tt-av{background:rgba(255,255,255,0.18)}
.aj-tt-text{font:inherit;font-family:"DM Sans",sans-serif;font-size:13px;width:100%;box-sizing:border-box;padding:9px 11px;border-radius:6px;border:0.5px solid rgba(7,39,45,0.3);resize:vertical;min-height:66px;margin-bottom:12px;color:#07272D}
.aj-tt-date{font:inherit;font-family:"DM Mono",monospace;font-size:13px;padding:8px 10px;border-radius:6px;border:0.5px solid rgba(7,39,45,0.3);margin-bottom:12px;color:#07272D}
.aj-tt-done{display:flex;align-items:center;gap:10px;margin-top:10px;padding:12px 14px;border-radius:10px;background:rgba(29,158,117,0.1);color:#11654b;font-family:"DM Sans",sans-serif;font-size:14px;font-weight:600;animation:aj-tt-in .22s ease both}
.aj-tt-done svg{animation:aj-tt-pop .35s ease both}
@keyframes aj-tt-pop{0%{transform:scale(.4);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
.aj-tt-q{font-family:"DM Sans",sans-serif;font-size:14px;font-weight:600;color:#07272D;margin-top:12px}
.aj-tt-more{display:flex;flex-wrap:wrap;align-items:center;gap:4px 14px;margin-top:10px;font-family:"DM Sans",sans-serif;font-size:12px;color:#8aa6b3}
.aj-tt-mbtn{font:inherit;display:inline-flex;align-items:center;gap:5px;font-size:12.5px;color:#3a5260;background:none;border:none;padding:4px 0;cursor:pointer;text-decoration:underline;text-decoration-color:rgba(58,82,96,0.35);text-underline-offset:3px}
.aj-tt-mbtn:hover:not(:disabled){color:#07272D;text-decoration-color:#07272D}
.aj-tt-mbtn:disabled{opacity:.45;cursor:default}
.aj-tt-err{font-family:"DM Sans",sans-serif;font-size:12px;color:#c0392b;margin:-4px 0 10px}
`

const initials = (n) => String(n || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()

export default function TriageTiles({ options, more = [], question, onConfirm, onFolded, disabled }) {
  const [chosen, setChosen] = useState(null)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(null)
  const opt = options.find(o => o.key === chosen) || more.find(o => o.key === chosen)
  const step = opt && opt.step

  function pick(o) {
    if (o.open) { o.open(); return }   // opens a tool to do the work; nothing decided here
    setChosen(o.key); setErr('')
    setValue(o.step && o.step.defaultValue != null ? o.step.defaultValue : '')
  }
  const needs = step && step.kind && step.kind !== 'confirm'
  const ready = !needs || (step.kind === 'custom' ? !!(step.ready && step.ready(value)) : step.kind === 'text' ? (step.optional || String(value).trim().length > 0) : !!value)

  async function go() {
    if (!ready || busy) return
    setBusy(true); setErr('')
    try {
      const msg = await onConfirm(opt.key, typeof value === 'string' ? value.trim() : value)
      setDone(msg || 'Done.')
      setTimeout(() => onFolded && onFolded(), 1500)
    } catch (e) { setErr(String(e && e.message || e)) } finally { setBusy(false) }
  }

  return (
    <div>
      <style>{CSS}</style>
      {done ? (
        <div className="aj-tt-done" role="status"><TileIcon name="check" size={22} />{done}</div>
      ) : !opt ? (<>
        {question && <div className="aj-tt-q">{question}</div>}
        <div className="aj-tt-grid">
          {options.map(o => (
            <button key={o.key} type="button" disabled={disabled || o.disabled} title={o.title} onClick={() => pick(o)}
              className={'aj-tt-tile' + (o.tone === 'primary' ? ' aj-tt-primary' : '') + (o.tone === 'caution' ? ' aj-tt-caution' : '')}>
              <span className="aj-tt-ic"><TileIcon name={o.icon} /></span>
              <span><div className="aj-tt-lab">{o.label}</div>{o.sub && <div className="aj-tt-sub">{o.sub}</div>}</span>
            </button>
          ))}
        </div>
        {more.length > 0 && (
          <div className="aj-tt-more">
            <span>Or:</span>
            {more.map(o => (
              <button key={o.key} type="button" className="aj-tt-mbtn" disabled={disabled || o.disabled} title={o.title} onClick={() => pick(o)}>
                <TileIcon name={o.icon} size={14} />{o.label}
              </button>
            ))}
          </div>
        )}
      </>) : (
        <div className="aj-tt-panel">
          <div className="aj-tt-ptitle"><TileIcon name={opt.icon} size={22} />{step.title}</div>
          <div className="aj-tt-pbody">{step.body}</div>
          {step.kind === 'person' && (
            <div className="aj-tt-people">
              {(step.people || []).map(p => (
                <button key={p.id} type="button" className={'aj-tt-person' + (value === p.id ? ' on' : '')} onClick={() => setValue(p.id)}>
                  <span className="aj-tt-av">{initials(p.name)}</span>{p.name}
                </button>
              ))}
            </div>
          )}
          {step.kind === 'text' && (
            <textarea className="aj-tt-text" autoFocus value={value} placeholder={step.placeholder || ''} onChange={e => setValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) go() }} />
          )}
          {step.kind === 'custom' && step.render(value, setValue)}
          {step.kind === 'date' && (
            <div><input type="date" className="aj-tt-date" value={value} min={step.min} onChange={e => setValue(e.target.value)} /></div>
          )}
          {err && <div className="aj-tt-err">{err}</div>}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="aj-tt-backbtn" onClick={() => { setChosen(null); setErr('') }} disabled={busy}><TileIcon name="back" size={16} />Choose again</button>
            <button type="button" className="aj-tt-go" disabled={!ready || busy} onClick={go}>
              <TileIcon name={step.confirmIcon || 'check'} size={18} />{busy ? 'Working…' : step.confirm(value)}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
